import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  Restaurant as RestaurantIcon,
  Cancel as CancelIcon,
  ShoppingBag as BagIcon,
  UpdateDisabled as DelayIcon,
  AccessTime as TimeIcon,
  Event as DateIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import OrderService from '../../../firebase/OrderService';
import { ORDER_STATUSES, ORDER_TYPES, PAYMENT_STATUSES, PAYMENT_METHODS } from '../../../models/order';

const OrdersAdminComponent = () => {
  // Estados
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState({
    from: "",
    to: ""
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [statsData, setStatsData] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
    totalSales: 0
  });

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchOrders();
  }, []);

  // Función para obtener pedidos
  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await OrderService.getTodayOrders();
      if (response && response.orders) {
        setOrders(response.orders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar los pedidos. Por favor, inténtalo de nuevo más tarde.',
        severity: 'error'
      });
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Pestañas de filtro rápido
  const tabs = [
    { label: "Todos", filter: () => true },
    { label: "Pendientes", filter: order => order.status === 'pending' },
    { label: "En preparación", filter: order => order.status === 'in_preparation' },
    { label: "Listos", filter: order => order.status === 'ready' },
    { label: "En reparto", filter: order => order.status === 'in_delivery' },
    { label: "Completados", filter: order => ['completed', 'delivered'].includes(order.status) }
  ];

  // Efecto para filtrar órdenes al cambiar criterios y calcular estadísticas
  useEffect(() => {
    filterOrders();
    calculateStats();
  }, [tabValue, searchTerm, selectedStatus, selectedType, selectedDateRange, orders]);

  // Función para calcular estadísticas
  const calculateStats = () => {
    if (orders.length === 0) {
      setStatsData({
        total: 0,
        pending: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        totalSales: 0
      });
      return;
    }

    const stats = {
      total: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      inProgress: orders.filter(o => ['confirmed', 'in_preparation', 'ready', 'in_delivery'].includes(o.status)).length,
      completed: orders.filter(o => ['completed', 'delivered'].includes(o.status)).length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      totalSales: orders
        .filter(o => ['completed', 'delivered', 'in_delivery', 'ready'].includes(o.status))
        .reduce((sum, order) => sum + order.total, 0)
    };

    setStatsData(stats);
  };

  // Función para filtrar órdenes
  const filterOrders = () => {
    if (orders.length === 0) {
      setFilteredOrders([]);
      return;
    }

    const tabFilter = tabs[tabValue].filter;
    
    let filtered = orders.filter(tabFilter);
    
    // Aplicar búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(searchLower) ||
        order.customer.name.toLowerCase().includes(searchLower) ||
        order.customer.phone.includes(searchTerm)
      );
    }
    
    // Filtrar por estado adicional (desde el diálogo de filtro)
    if (selectedStatus) {
      filtered = filtered.filter(order => order.status === selectedStatus);
    }
    
    // Filtrar por tipo
    if (selectedType) {
      filtered = filtered.filter(order => order.type === selectedType);
    }
    
    // Filtrar por rango de fechas
    if (selectedDateRange.from) {
      filtered = filtered.filter(order => order.date >= selectedDateRange.from);
    }
    if (selectedDateRange.to) {
      filtered = filtered.filter(order => order.date <= selectedDateRange.to);
    }
    
    setFilteredOrders(filtered);
  };

  // Manejar cambio de pestaña
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Ver detalles del pedido
  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  // Actualizar estado del pedido
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    setIsLoading(true);
    
    try {
      await OrderService.updateOrderStatus(orderId, newStatus);
      
      // Actualizar el estado en la vista
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus } 
          : order
      );
      
      setOrders(updatedOrders);
      
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      
      setSnackbar({
        open: true,
        message: `Estado del pedido ${orderId} actualizado a "${ORDER_STATUSES[newStatus.toUpperCase()]?.label}"`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error al actualizar el estado del pedido ${orderId}:`, error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar el estado del pedido. Por favor, inténtalo de nuevo.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Renderizar acciones disponibles según el estado del pedido
  const renderOrderActions = (order) => {
    const actions = [];
    
    switch (order.status) {
      case 'pending':
        actions.push(
          <Button 
            key="confirm" 
            size="small" 
            startIcon={<CheckIcon />} 
            onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}
          >
            Confirmar
          </Button>
        );
        actions.push(
          <Button 
            key="cancel" 
            size="small" 
            color="error" 
            startIcon={<CancelIcon />} 
            onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
          >
            Cancelar
          </Button>
        );
        break;
      case 'confirmed':
        actions.push(
          <Button 
            key="prepare" 
            size="small" 
            color="secondary" 
            startIcon={<RestaurantIcon />} 
            onClick={() => handleUpdateOrderStatus(order.id, 'in_preparation')}
          >
            Preparar
          </Button>
        );
        break;
      case 'in_preparation':
        actions.push(
          <Button 
            key="ready" 
            size="small" 
            color="success" 
            startIcon={<CheckIcon />} 
            onClick={() => handleUpdateOrderStatus(order.id, 'ready')}
          >
            Listo
          </Button>
        );
        break;
      case 'ready':
        if (order.type === 'delivery') {
          actions.push(
            <Button 
              key="deliver" 
              size="small" 
              color="primary" 
              startIcon={<ShippingIcon />} 
              onClick={() => handleUpdateOrderStatus(order.id, 'in_delivery')}
            >
              En reparto
            </Button>
          );
        } else {
          actions.push(
            <Button 
              key="complete" 
              size="small" 
              startIcon={<CheckIcon />} 
              onClick={() => handleUpdateOrderStatus(order.id, 'completed')}
            >
              Completar
            </Button>
          );
        }
        break;
      case 'in_delivery':
        actions.push(
          <Button 
            key="delivered" 
            size="small" 
            color="success" 
            startIcon={<CheckIcon />} 
            onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
          >
            Entregado
          </Button>
        );
        break;
      default:
        break;
    }
    
    return actions;
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Encabezado y Acciones Principales */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Gestión de Pedidos</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFilterDialogOpen(true)}
            sx={{ ml: 2 }}
          >
            Filtros
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Pedidos Totales (Hoy)</Typography>
              <Typography variant="h4">{statsData.total}</Typography>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Chip 
                  size="small" 
                  label={`${statsData.pending} pendientes`} 
                  color="warning"
                />
                <Chip 
                  size="small" 
                  label={`${statsData.cancelled} cancelados`} 
                  color="error"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">En Proceso</Typography>
              <Typography variant="h4">{statsData.inProgress}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.status === 'in_preparation').length} en preparación
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.status === 'in_delivery').length} en reparto
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Completados</Typography>
              <Typography variant="h4">{statsData.completed}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.type === 'local').length} en local
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.type === 'takeaway' || o.type === 'delivery').length} para llevar/domicilio
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Ventas</Typography>
              <Typography variant="h4">{statsData.totalSales.toFixed(2)}€</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.paymentStatus === 'paid').length} pagados
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {orders.filter(o => o.paymentStatus === 'pending').length} pendientes de pago
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Buscar por ID, cliente o teléfono..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Pestañas de filtro rápido */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {tabs.map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>
      {/* Tabla de pedidos */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredOrders.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{order.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{order.date}</Typography>
                      <Typography variant="body2" color="textSecondary">{order.time}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">{order.customer.name}</Typography>
                      <Typography variant="body2" color="textSecondary">{order.customer.phone}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={ORDER_TYPES[order.type.toUpperCase()]?.icon}
                      label={ORDER_TYPES[order.type.toUpperCase()]?.label}
                      size="small"
                    />
                    {order.type === 'local' && (
                      <Typography variant="body2" color="textSecondary">{order.table}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{order.total.toFixed(2)}€</TableCell>
                  <TableCell>
                    <Chip 
                      label={ORDER_STATUSES[order.status.toUpperCase()]?.label}
                      color={ORDER_STATUSES[order.status.toUpperCase()]?.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleViewOrderDetails(order)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                      {renderOrderActions(order)}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No hay pedidos disponibles
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            Los pedidos aparecerán aquí cuando los clientes realicen sus compras.
          </Typography>
        </Paper>
      )}
      {/* Diálogo de Detalles del Pedido */}
      <Dialog
        open={orderDetailOpen}
        onClose={() => setOrderDetailOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Pedido {selectedOrder.id}
                </Typography>
                <Chip 
                  label={ORDER_STATUSES[selectedOrder.status.toUpperCase()]?.label}
                  color={ORDER_STATUSES[selectedOrder.status.toUpperCase()]?.color}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                    Datos del Cliente
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="body1">{selectedOrder.customer.name}</Typography>
                    <Typography variant="body2">{selectedOrder.customer.phone}</Typography>
                    <Typography variant="body2">{selectedOrder.customer.email}</Typography>
                    {selectedOrder.customer.address && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedOrder.customer.address}
                      </Typography>
                    )}
                  </Paper>

                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TimeIcon fontSize="small" sx={{ mr: 1 }} />
                    Información del Pedido
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Tipo</Typography>
                        <Typography variant="body1">
                          {ORDER_TYPES[selectedOrder.type.toUpperCase()]?.label}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Fecha y Hora</Typography>
                        <Typography variant="body1">
                          {selectedOrder.date} {selectedOrder.time}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Método de Pago</Typography>
                        <Typography variant="body1">
                          {selectedOrder.paymentMethod === 'card' ? 'Tarjeta' : 'Efectivo'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Estado de Pago</Typography>
                        <Chip 
                          size="small" 
                          label={selectedOrder.paymentStatus === 'paid' ? 'Pagado' : 'Pendiente'}
                          color={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'}
                        />
                      </Grid>
                      {selectedOrder.type === 'local' && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Mesa</Typography>
                          <Typography variant="body1">{selectedOrder.table}</Typography>
                        </Grid>
                      )}
                      {selectedOrder.notes && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">Notas</Typography>
                          <Typography variant="body1">{selectedOrder.notes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Productos
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 0, mb: 3 }}>
                    <List sx={{ width: '100%', p: 0 }}>
                      {selectedOrder.items.map((item, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemText
                              primary={item.name}
                              secondary={`${item.quantity} x ${item.price.toFixed(2)}€`}
                            />
                            <ListItemSecondaryAction>
                              <Typography variant="subtitle2">
                                {(item.quantity * item.price).toFixed(2)}€
                              </Typography>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < selectedOrder.items.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">Subtotal</Typography>
                      <Typography variant="body1">
                        {selectedOrder.total.toFixed(2)}€
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1">IVA (10%)</Typography>
                      <Typography variant="body1">
                        {(selectedOrder.total * 0.10).toFixed(2)}€
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {selectedOrder.total.toFixed(2)}€
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                startIcon={<PrintIcon />}
                onClick={() => {}}
              >
                Imprimir
              </Button>
              <Box sx={{ flex: '1 1 auto' }} />
              {renderOrderActions(selectedOrder)}
              <Button onClick={() => setOrderDetailOpen(false)}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Diálogo de Filtros */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
      >
        <DialogTitle>Filtros</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-select-label">Estado</InputLabel>
            <Select
              labelId="status-select-label"
              value={selectedStatus}
              label="Estado"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.values(ORDER_STATUSES).map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="type-select-label">Tipo de Pedido</InputLabel>
            <Select
              labelId="type-select-label"
              value={selectedType}
              label="Tipo de Pedido"
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.values(ORDER_TYPES).map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rango de Fechas
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Desde"
                  type="date"
                  value={selectedDateRange.from}
                  onChange={(e) => setSelectedDateRange({...selectedDateRange, from: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Hasta"
                  type="date"
                  value={selectedDateRange.to}
                  onChange={(e) => setSelectedDateRange({...selectedDateRange, to: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setSelectedStatus("");
              setSelectedType("");
              setSelectedDateRange({ from: "", to: "" });
            }}
          >
            Limpiar
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersAdminComponent;